/**
 * HecProductTileCms — unified product tile for CMS contexts (carousels,
 * recommendations, Builder blocks). Uses HecProduct type.
 *
 * Composes shared sub-components: ProductBadges, Ratings, ProductTilePrice,
 * AddToCart, ProductMessaging, OosOverlay, ProductTileSkeleton. Two owner
 * classes dispatch to their own standalone clone instead: LMN products to
 * `LmnProductTile` (no price/ATC — SFCC-11677), and telehealth products to
 * `TelehealthProductTile` (Builder-authored price, intake CTA, no ATC/quick
 * view — ET-225..232).
 *
 * The PLP tile (at routes/product-list/components/product-tile.tsx) uses
 * ProductHitWithSwatches and has its own swatch/quick-view integration.
 * Both tiles share the same sub-components for visual consistency.
 *
 * Source references:
 * - hec-composable/hec/components/product-tile-cms/productTileCms.tsx
 * - sfcc-website/org_hec/cartridge/templates/default/product/productTile.isml
 */
import { useMemo } from "react";
import { Link, type LinkProps } from "react-router";

import { ProductImage } from "~/components/product-list/product-image";
import { TriButton } from "~/components/ui/tri-button";
import { useSite, useURL } from "~/contexts";
import { emitProductClick } from "~/hec/analytics/event-layer/gtm-product-click-events";
import { ProductTilePrice } from "~/hec/components/product-tile/product-tile-price";
import type { HecProduct } from "~/hec/types/product";
import {
  getProductBadges,
  isLMNProduct,
  isOutOfStockOverride,
  isTelehealthProduct,
  isVariantProduct,
} from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { LOW_STOCK_NUM } from "~/lib/constants";
import { cn } from "~/lib/utils";
import { FavoritesCTA } from "~/routes/resources/favorites";
import { resolvePriceFromHecProduct } from "~/shop/utils/price-display";

import { AddToCart } from "./add-to-cart";
import { LmnProductTile } from "./lmn-product-tile";
import { OosOverlay } from "./oos-overlay";
import { ProductBadges } from "./product-badges";
import { ProductMessaging } from "./product-messaging";
import { ProductTileSkeleton } from "./product-tile-skeleton";
import { Ratings } from "./ratings";
import { TelehealthProductTile } from "./telehealth-product-tile";

interface HecProductTileCmsProps {
  product: HecProduct;
  /** Show skeleton loading state */
  isLoading?: boolean;
  /** Show add-to-cart controls (default true) */
  enableAddToCart?: boolean;
  /** Show ratings (default true; set false for basic/compact view) */
  enableRatings?: boolean;
  /** Star-color variant for the ratings — "recs" on recommendation carousels,
   *  "default" (PDP/PLP color) otherwise. */
  ratingsVariant?: "default" | "recs";
  /** Show favorites heart CTA (default true; recs carousels set false to match
   *  production, which omits the heart on recommendation tiles) */
  enableFavorites?: boolean;
  /** Show product messaging — promotions, variant info, compare-to */
  enableMessaging?: boolean;
  /** Show the "Only N left!" badge when stock is low (default true) */
  enableLowStockBadge?: boolean;
  /** Show the Quick View button (default true). Off keeps `onOpenQuickView`
   *  for add-to-cart on a master, which must pick a variant (SFCC-11588). */
  enableQuickView?: boolean;
  /** Callback to open quick view (for master products). `quantity` is set when
   *  the trigger was the tile's AddToCart — the QV pre-fills it (SFCC-11825). */
  onOpenQuickView?: (quantity?: number) => void;
  /** Site ID override — defaults to SiteContext site.id */
  siteId?: string;
  /** Additional class names on the root element */
  className?: string;
  /** Inline styles (e.g., border color for carousel tiles) */
  style?: React.CSSProperties;
  /** Favorites page mode: hides heart CTA, shows variant values + Remove button */
  favoritesMode?: boolean;
  /** Hide Quick View below the `lg` breakpoint. Opt-in. No current caller —
   *  the HSA mobile-hide (AB-test winner SFCC-10286) was reverted so quick view
   *  shows on mobile across all sites; kept as a valid opt-in API. */
  hideQuickViewOnMobile?: boolean;
  /** Route prefetch strategy for the product link. Default `intent`
   *  (hover/focus). `viewport` (SFCC-11297) is no longer passed by any grid
   *  surface — it fired a PDP loader per tile scrolled into view (isolate-OOM
   *  amplifier); don't reintroduce it without edge-caching the .data path. */
  prefetch?: LinkProps["prefetch"];
  /** Forwarded to AddToCart's QuantityStepper. Hides "−" at qty=1 to save
   *  horizontal space in narrow tile placements (e.g. ATC modal carousel). */
  collapseStepperAtMin?: boolean;
  /** Forwarded to AddToCart. Renders a compact "Add" label at every width
   *  (recs carousels in narrow columns). */
  compactAddLabel?: boolean;
  /** GTM product-click list context (SFCC-11026). When `gtmListId` is set, a
   *  tile-link click emits `productClick` + `GA4_productClick` for this list. */
  gtmListId?: string;
  gtmListName?: string;
  gtmIndex?: number;
  /** COM-158: above-the-fold tiles (first PLP row) render their image eager +
   *  fetchPriority="high" so they become LCP candidates. Default lazy. */
  imagePriority?: boolean;
}

export function HecProductTileCms({
  product,
  isLoading = false,
  enableAddToCart = true,
  enableRatings = true,
  ratingsVariant = "default",
  enableFavorites = true,
  enableMessaging = false,
  enableLowStockBadge = true,
  enableQuickView = true,
  onOpenQuickView,
  siteId: siteIdProp,
  className,
  style,
  favoritesMode = false,
  hideQuickViewOnMobile = false,
  prefetch = "intent",
  collapseStepperAtMin = false,
  compactAddLabel = false,
  gtmListId,
  gtmListName,
  gtmIndex,
  imagePriority = false,
}: HecProductTileCmsProps) {
  const url = useURL();
  const { site } = useSite();
  const siteId = siteIdProp ?? site.id;
  // Stable reference so the memoized <ProductTilePrice> skips re-render when the
  // product is unchanged (resolver returns a fresh object each call).
  const priceDisplay = useMemo(
    () => resolvePriceFromHecProduct(product),
    [product],
  );

  if (isLoading || product.skeleton) {
    return (
      <div
        className={cn("overflow-hidden rounded-md border bg-white", className)}
        style={style}
      >
        <ProductTileSkeleton />
      </div>
    );
  }

  // LMN (Letter of Medical Necessity) products render a distinct tile — no price,
  // no ATC, no quantity/swatches; a "What's an LMN?" modal + "Get Started" CTA
  // instead (SFCC-11677). Matches live SFRA, which branches on `isLMNProduct`
  // alone (viewer-independent — the allow-list only gates LMN *visibility* via the
  // CIO filter, not how the tile renders). Dispatch to the standalone clone.
  if (isLMNProduct(product)) {
    return (
      <LmnProductTile
        product={product}
        enableRatings={enableRatings}
        ratingsVariant={ratingsVariant}
        enableFavorites={!favoritesMode && enableFavorites}
        favoritesMode={favoritesMode}
        siteId={siteId}
        className={className}
        style={style}
        prefetch={prefetch}
        imagePriority={imagePriority}
      />
    );
  }

  // Telehealth (GLP-1) products render their own tile — Builder-authored price,
  // "Get Started" into intake, and no ATC / quantity / favorites / quick view
  // (ET-225..232, ET-297). Same clone-over-flags dispatch as LMN above, keyed on
  // the product-level `c_ownerClass` so it applies on every tile surface.
  // Ordered AFTER the LMN branch: the two owner classes are mutually exclusive,
  // and LMN is the older, wider-reaching treatment.
  if (isTelehealthProduct(product)) {
    return (
      <TelehealthProductTile
        product={product}
        enableRatings={enableRatings}
        ratingsVariant={ratingsVariant}
        siteId={siteId}
        className={className}
        style={style}
        prefetch={prefetch}
        imagePriority={imagePriority}
        gtmListId={gtmListId}
        gtmListName={gtmListName}
        gtmIndex={gtmIndex}
      />
    );
  }

  const isSoldOut = isOutOfStockOverride(product);
  const badges = getProductBadges(product);
  const productName = product.name || product.productName || "";
  const productUrl = product.url || buildProductUrl(product.id, productName);
  const imageUrl = product.image_url || product.image?.link;
  const tileImage = product.image
    ? { link: product.image.link, alt: product.image.alt ?? productName }
    : imageUrl
      ? { link: imageUrl, alt: productName }
      : undefined;

  // GTM product-tile click (SFCC-11026): dual `productClick` (UA) +
  // `GA4_productClick` (GA4). Emit is no-op'd inside unless a surface supplied
  // `gtmListId`. Master `item_id`. `category` is real (SFCC-12141) only when
  // the caller's data source enriched `categoryPath` (currently the primary
  // PLP/search grid only) — other surfaces still fall back to "".
  //
  // `emitProductClick` fans this one value out to THREE sinks: GA4
  // `item_category..4` (split), the legacy UA `productClick.category` (joined
  // path), and Segment `Product Clicked.category` (joined path). Populating all
  // three is intended, not incidental fallout: all three were blank before, GA4
  // and UA both match SFRA's own joined-path category, and SFRA fires no
  // `Product Clicked` at all so there's no Segment contract to diverge from.
  //
  // Hoisted out of the click handler (SFCC-12175 W6) because the favorites
  // heart below needs the SAME item shape for `add_to_wishlist`. One
  // definition, two consumers — a second inline copy would drift.
  const ga4TileItem = (() => {
    const p = product as {
      master?: { masterId?: string };
      c_parentMasterProductID?: string;
    };
    return {
      id: p.master?.masterId ?? p.c_parentMasterProductID ?? product.id,
      name: productName,
      brand: product.brand,
      variant: isVariantProduct(product) ? product.id : null,
      // priceDisplay is null for a product with no positive price (COM-343 —
      // Sept-1 pricebook expiry made these real in CIO grids); the old cast
      // hid the nullability from TS and crashed SSR.
      price: priceDisplay?.price,
      category: product.categoryPath ?? "",
    };
  })();

  const ga4ListCtx = {
    listId: gtmListId ?? "",
    listName: gtmListName ?? gtmListId ?? "",
    index: gtmIndex ?? 1,
  };

  const handleProductClick = () => {
    emitProductClick(ga4TileItem, { ...ga4ListCtx, siteId });
  };

  // `add_to_wishlist` payload for the heart (SFCC-12175 W6). Same item as the
  // click event, plus this tile's list context — but only when the surface
  // actually supplied one. Spreading it unconditionally would report
  // `index: 1` / Segment `position: 1` for a tile with no list at all, while
  // `emitProductClick` correctly no-ops for that same tile.
  const wishlistGa4 = gtmListId
    ? { ...ga4TileItem, ...ga4ListCtx }
    : ga4TileItem;
  const openQuickView = enableQuickView ? onOpenQuickView : undefined;

  return (
    <div
      className={cn("overflow-hidden rounded-md bg-white", className)}
      style={style}
      data-testid={`product-tile-root-${product.id}`}
    >
      {/* Image container with badges, favorites, quick view, OOS overlay */}
      <div
        className="aspect-square group relative w-full"
        role={openQuickView ? "button" : undefined}
        tabIndex={openQuickView ? 0 : undefined}
        onKeyDown={e => {
          if ((e.key === "Enter" || e.key === " ") && openQuickView) {
            openQuickView();
          }
        }}
      >
        <OosOverlay active={isSoldOut} />

        {badges.length > 0 && (
          <ProductBadges badges={badges} productId={product.id} />
        )}

        {!favoritesMode && enableFavorites && (
          <FavoritesCTA
            productId={product.id}
            className="absolute right-2 top-2 z-10"
            iconHeight={22}
            iconWidth={22}
            noBorder
            wishlistGa4={wishlistGa4}
          />
        )}

        <Link
          to={url(productUrl)}
          prefetch={prefetch}
          state={{ tileProduct: product }}
          className="block"
          data-testid={`product-tile-image-${product.id}`}
          onClick={handleProductClick}
        >
          {tileImage ? (
            <ProductImage
              image={tileImage}
              type="plp"
              priority={imagePriority}
            />
          ) : (
            <div className="aspect-square flex items-center justify-center rounded text-sm text-gray-400">
              No image
            </div>
          )}
        </Link>

        {/* Quick View button */}
        {openQuickView && !isSoldOut && (
          <button
            type="button"
            // Wrapped so the MouseEvent isn't passed as the `quantity` param.
            onClick={() => openQuickView()}
            className={cn(
              "absolute bottom-0 left-0 z-10 flex w-full items-center justify-center bg-gray-02 py-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100",
              hideQuickViewOnMobile && "max-lg:hidden",
            )}
            data-testid={`product-tile-quick-view-${product.id}`}
          >
            <span className="font-grotesque text-[10px] font-medium uppercase tracking-wide text-blue-03">
              QUICK VIEW
            </span>
          </button>
        )}
      </div>

      {/* Tile body */}
      <div className="px-2 pb-2 pt-2 text-left">
        {/* Ratings */}
        {enableRatings && (
          <div className="mt-2 flex">
            <Ratings
              rating={product.rating ?? 0}
              reviewCount={product.reviewCount}
              siteId={siteId}
              size="small"
              variant={ratingsVariant}
            />
          </div>
        )}

        {/* Product name — wrapper reserves 2-line height for grid alignment;
            min-h lives here to avoid breaking -webkit-line-clamp on the Link */}
        <div className="mt-2 min-h-[3rem]">
          <Link
            to={url(productUrl)}
            prefetch={prefetch}
            state={{ tileProduct: product }}
            className="mb-[.5rem] line-clamp-2 font-grotesque font-medium leading-6 text-blue-04"
            data-testid={`product-tile-name-${product.id}`}
            onClick={handleProductClick}
          >
            {productName}
          </Link>
        </div>

        {/* Variant values — favorites page only */}
        {favoritesMode &&
          isVariantProduct(product) &&
          product.variationValues &&
          Object.entries(product.variationValues).map(([key, value]) => (
            <p
              key={`${key}-${value}`}
              className="text-dark-on-light pt-2 text-left text-xs font-light capitalize"
            >
              {key}: {String(value)}
            </p>
          ))}

        {/* Price */}
        <ProductTilePrice
          display={priceDisplay}
          className="mb-[.5rem]"
          data-testid={`product-tile-price-${product.id}`}
        />

        {/* Add to Cart — in favorites mode, variant products direct-add (no quick view) */}
        {enableAddToCart && (
          <AddToCart
            product={product}
            isProductTile
            onOpenQuickView={
              favoritesMode && isVariantProduct(product)
                ? undefined
                : onOpenQuickView
            }
            collapseStepperAtMin={collapseStepperAtMin}
            compactAddLabel={compactAddLabel}
          />
        )}

        {/* Remove button — favorites page only */}
        {favoritesMode && (
          <FavoritesCTA
            productId={product.id}
            className="flex"
            isFavoritesPage={true}
            wishlistGa4={wishlistGa4}
          >
            <TriButton variant="link" className="self-end hover:no-underline">
              <span className="text-sm font-light text-remove hover:underline">
                Remove
              </span>
            </TriButton>
          </FavoritesCTA>
        )}

        {/* Low stock indicator */}
        {enableLowStockBadge &&
          product.inventory?.stockLevel != null &&
          product.inventory.stockLevel > 0 &&
          product.inventory.stockLevel < LOW_STOCK_NUM && (
            <span className="mt-1 inline-block rounded-[3px] border border-accentalpha-7 bg-surface-accent-light px-2 text-xs text-accentalpha-11">
              Only {product.inventory.stockLevel} left!
            </span>
          )}

        {/* Product messaging */}
        {enableMessaging && <ProductMessaging product={product} />}
      </div>
    </div>
  );
}
